import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Trophy, Flame, Award, Sparkles, Users } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';
import { useNavigate } from 'react-router-dom';

const ICONS = {
  trophy: Trophy,
  flame: Flame,
  award: Award,
  sparkles: Sparkles,
  level_up: Trophy,
  streak_milestone: Flame,
  badge: Award,
  badge_earned: Award,
  phrase_saved: Sparkles,
};

const relTime = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
};

/**
 * Drop-in card that shows recent activity from a user's friends:
 * level-ups, streak milestones, badges, and any explicit `user_activities`
 * we capture elsewhere. Pulls /api/friends/activity-feed.
 *
 * Hidden gracefully if the user has no friends yet.
 */
export const FriendsActivityFeed = ({ limit = 8 }) => {
  const navigate = useNavigate();
  const [data, setData] = useState({ activities: [], friend_count: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    apiFetch(`/api/friends/activity-feed?limit=${limit}`).then(({ data, error }) => {
      if (!alive) return;
      if (!error && data) setData(data);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [limit]);

  if (loading) {
    return (
      <Card data-testid="friends-activity-loading">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4 text-[#00A896]" /> Friends activity
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-3/4" />
        </CardContent>
      </Card>
    );
  }

  if (data.friend_count === 0) {
    return (
      <Card className="border-dashed" data-testid="friends-activity-empty">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4 text-[#00A896]" /> Friends activity
          </CardTitle>
          <CardDescription>
            Add a friend with their friend code to see their streaks, badges, and level-ups here.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <button
            onClick={() => navigate('/friends')}
            className="text-sm text-[#00A896] hover:underline"
            data-testid="friends-activity-add-friend"
          >
            Add a friend →
          </button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="friends-activity-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Users className="h-4 w-4 text-[#00A896]" /> Friends activity
        </CardTitle>
        <CardDescription>
          {data.friend_count} friend{data.friend_count === 1 ? '' : 's'} · recent moments
        </CardDescription>
      </CardHeader>
      <CardContent>
        {data.activities.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">No new activity. Check back soon.</p>
        ) : (
          <ul className="space-y-2">
            {data.activities.map((a, i) => {
              const Icon = ICONS[a.icon] || ICONS[a.type] || Sparkles;
              return (
                <li
                  key={i}
                  className="flex items-start gap-2 text-sm py-1"
                  data-testid={`friends-activity-${i}`}
                >
                  <span className="shrink-0 mt-0.5 w-7 h-7 rounded-full bg-[#00E5CC]/10 flex items-center justify-center">
                    <Icon className="h-3.5 w-3.5 text-[#00A896]" />
                  </span>
                  <span className="flex-1 leading-snug">
                    <strong>{a.name || 'A friend'}</strong> {a.text}
                    <span className="block text-[11px] text-muted-foreground">{relTime(a.at)}</span>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default FriendsActivityFeed;
